// NotificationBell.tsx
import React from "react";
import { Bell } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { useTranslation } from "react-i18next";
import { useNotifications } from "../../hooks/useNotifications";
import { NotificationDropdown } from "../notifications/NotificationDropdown";
import { RemindersTab } from "../notifications/RemindersTab";

export const NotificationBell: React.FC = () => {
  const { t } = useTranslation();
  const {
    notifications,
    unreadCount,
    isLoading,
    hasMore,
    soundEnabled,
    toggleSound,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllRead,
    audioRef,
  } = useNotifications();

  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <>
      {/* Hidden audio element for notification sound */}
      <audio
        ref={audioRef}
        src="/assets/sounds/notification.mp3"
        preload="auto"
      />

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            aria-label={`Notifications${
              unreadCount > 0 ? ` (${unreadCount} unread)` : ""
            }`}
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <Badge
                variant="destructive"
                className="absolute -top-1 -right-1 h-5 min-w-[20px] flex items-center justify-center p-0 text-xs font-semibold animate-pulse"
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[calc(100vw-2rem)] md:w-96 p-0"
          align="end"
          side="bottom"
          sideOffset={8}
        >
          <Tabs defaultValue="notifications" className="flex flex-col h-[500px]">
            {/* Tabs Header */}
            <div className="flex-shrink-0 border-b">
              <TabsList className="grid w-full h-12 grid-cols-2 p-0 bg-transparent rounded-none">
                <TabsTrigger
                  value="notifications"
                  className="relative rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary transition-all"
                >
                  <span className="font-medium">{t("notifications")}</span>
                  {unreadCount > 0 && (
                    <span className="ml-2 text-xs bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 min-w-[20px] text-center font-medium animate-pulse">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger
                  value="reminders"
                  className="relative rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary transition-all"
                >
                  <span className="font-medium">{t("reminders")}</span>
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Notifications Tab */}
            <TabsContent value="notifications" className="flex-1 m-0 overflow-hidden">
              <NotificationDropdown
                notifications={notifications}
                unreadCount={unreadCount}
                isLoading={isLoading}
                hasMore={hasMore}
                soundEnabled={soundEnabled}
                toggleSound={toggleSound}
                onMarkAsRead={markAsRead}
                onMarkAllAsRead={markAllAsRead}
                onDelete={deleteNotification}
                onDeleteAllRead={deleteAllRead}
                onLoadMore={() =>
                  fetchNotifications(false, notifications.length)
                }
                onClose={() => setIsOpen(false)}
              />
            </TabsContent>

            {/* Reminders Tab */}
            <TabsContent value="reminders" className="flex-1 m-0 overflow-hidden">
              <RemindersTab onClose={() => setIsOpen(false)} />
            </TabsContent>
          </Tabs>
        </PopoverContent>
      </Popover>
    </>
  );
};